export type IReadyEvent = void;

export interface CustomEventMap extends HTMLElementEventMap {
    ready: CustomEvent<IReadyEvent>;
}

export interface ElementBase {
    addEventListener<K extends keyof CustomEventMap>(
        event: K,
        listener: ((this: this, ev: CustomEventMap[K]) => unknown) | null,
        options?: AddEventListenerOptions | boolean
    ): void;
    addEventListener(
        type: string,
        callback: EventListenerOrEventListenerObject | null,
        options?: AddEventListenerOptions | boolean
    ): void;
    removeEventListener<K extends keyof CustomEventMap>(
        type: K,
        listener: (this: this, ev: CustomEventMap[K]) => unknown,
        options?: boolean | EventListenerOptions
    ): void;
    removeEventListener(
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | EventListenerOptions
    ): void;
    dispatchEvent<EventType extends CustomEventMap[keyof CustomEventMap]>(event: EventType): boolean;
}

export abstract class ElementBase extends HTMLElement {
    public abstract is(): string;

    protected abstract template(): HTMLTemplateElement;
    private static readonly TEMPLATE_CACHE: {
        [name: string]: HTMLTemplateElement;
    } = {};
    private memoizedTemplate(): HTMLTemplateElement {
        const is = this.is();
        if (ElementBase.TEMPLATE_CACHE[is]) {
            return ElementBase.TEMPLATE_CACHE[is];
        }
        const template = this.template();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).ShadyCSS) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).ShadyCSS.prepareTemplate(template, this.is());
        }
        ElementBase.TEMPLATE_CACHE[is] = template;
        return template;
    }

    public connectedCallback() {
        const template = this.memoizedTemplate();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).ShadyCSS) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).ShadyCSS.styleElement(this);
        }
        if (!this.shadowRoot) {
            this.attachShadow({ mode: "open" });
            this.shadowRoot.appendChild(document.importNode(template.content, true));
            requestAnimationFrame(() => this.dispatchEvent(new CustomEvent<IReadyEvent>("ready")));
        }
    }

    public whenReady(actor: () => void) {
        if (this.shadowRoot) {
            actor();
        } else {
            this.addEventListener("ready", () => actor());
        }
    }
}
